import React, { useCallback, useState } from 'react';
import { Badge, Box, Flex, Image, Stack, Text, Collapsible } from '@chakra-ui/react';
import { CaretDown, CaretUp, Check, X } from '@phosphor-icons/react';

import aiIcon from '@/assets/icon/ai-icon.svg';

import {
  AlertsAndFollowUpQuestion,
  OverviewAnalysisResult,
  ResumeAnalysisResult
} from '@/types/AnalysisResult';

import { ApplicationQuestionAnswer } from '@/types/ApplicationQuestionAnswer';

import {
  CandidateDetailsTab,
  getApplicationQuestionTypeLabel,
  getFollowUpQuestionStatusColor,
  getFollowUpQuestionStatusLabel
} from '@/types/enums';

import { RequirementMatchCollapsible } from '@/views/Candidate/components';

const FollowUpItemsCard = ({
  followUpItem,
  onClick
}: {
  followUpItem: AlertsAndFollowUpQuestion;
  onClick?: (tab: CandidateDetailsTab) => void;
}) => {
  const statusColor = getFollowUpQuestionStatusColor(followUpItem.status);

  const handleClick = useCallback(() => {
    if (!onClick) return;
    const targetTab = followUpItem.feedback
      ? CandidateDetailsTab.Interview
      : CandidateDetailsTab.Resume;
    onClick(targetTab);
  }, [onClick, followUpItem.feedback]);

  return (
    <Box
      border="1px solid"
      borderColor="neutrals.200"
      borderRadius="8px"
      p="16px"
      bg="neutrals.50"
      maxW="300px"
      flex="1"
      display="flex"
      flexDirection="column"
      transition="all 0.2s ease-in-out"
      _hover={{ boxShadow: 'sm' }}
      cursor="pointer"
      onClick={handleClick}
    >
      <Stack gap="12px" w="100%" flex="1">
        <Box
          display="flex"
          alignItems="center"
          fontSize="12px"
          color={statusColor.FontColor}
          border={`1px solid ${statusColor.BorderColor}`}
          borderRadius="8px"
          p="2px 8px"
          bg={statusColor.BackgroundColor}
          minW="fit-content"
          justifyContent="center"
          w="fit-content"
        >
          {getFollowUpQuestionStatusLabel(followUpItem.status)}
        </Box>
        <Text fontSize="16px" fontWeight="semibold" color="neutrals.900">
          {followUpItem.title}
        </Text>
        <Text fontSize="14px" color="neutrals.700">
          {followUpItem.feedback || followUpItem.alert}
        </Text>
      </Stack>
    </Box>
  );
};

export default function CandidateSummary({
  resumeAnalysisResult,
  overviewAnalysisResult,
  followUpQuestions,
  applicationQuestionAnswers,
  onNavigateToFollowUp
}: {
  analysis_overview?: string;
  resumeAnalysisResult: ResumeAnalysisResult;
  overviewAnalysisResult?: OverviewAnalysisResult;
  followUpQuestions: AlertsAndFollowUpQuestion[];
  applicationQuestionAnswers?: ApplicationQuestionAnswer[];
  onNavigateToFollowUp?: (tab: CandidateDetailsTab) => void;
}) {
  const [alignedItemsOpen, setAlignedItemsOpen] = useState(true);
  const [misalignedItemsOpen, setMisalignedItemsOpen] = useState(true);
  const [applicationQuestionsOpen, setApplicationQuestionsOpen] = useState(false);

  const handleFollowUpClick = useCallback(
    (tab: CandidateDetailsTab) => {
      if (onNavigateToFollowUp) {
        onNavigateToFollowUp(tab);
      }
    },
    [onNavigateToFollowUp]
  );

  return (
    <Flex direction="column" w="100%" mt="40px">
      <Flex mb="12px">
        <Image src={aiIcon} w="20px" h="20px" />
        <Box color="neutrals.900" fontSize="18px" fontWeight="semibold" ml="8px">
          AI Overview
        </Box>
      </Flex>
      <Text
        id="ai-overview"
        fontSize="16px"
        color="neutrals.700"
        p={0}
        mb="32px"
        whiteSpace="pre-wrap"
      >
        {overviewAnalysisResult?.overview || resumeAnalysisResult?.overview}
      </Text>
      {applicationQuestionAnswers && applicationQuestionAnswers.length > 0 && (
        <Box mb="32px">
          <Box border="1px solid" borderColor="neutrals.200" borderRadius="12px" p={0} w="100%">
            <Collapsible.Root
              open={applicationQuestionsOpen}
              onOpenChange={({ open }) => setApplicationQuestionsOpen(open)}
            >
              <Collapsible.Trigger
                w="100%"
                px={{ base: '16px', md: '24px' }}
                py={{ base: '12px', md: '16px' }}
                bg="neutrals.50"
                cursor="pointer"
                borderBottom={applicationQuestionsOpen ? '1px solid' : '0px'}
                borderColor="neutrals.200"
                borderRadius="12px"
                borderBottomRadius={applicationQuestionsOpen ? '0px' : '12px'}
              >
                <Flex justifyContent="space-between" alignItems="center" bg="neutrals.50">
                  <Box
                    color="neutrals.700"
                    fontSize={{ base: '16px', md: '18px' }}
                    fontWeight="600"
                  >
                    Application Questions
                  </Box>
                  {applicationQuestionsOpen ? (
                    <CaretUp size={32} color="#242424" />
                  ) : (
                    <CaretDown size={32} color="#242424" />
                  )}
                </Flex>
              </Collapsible.Trigger>
              <Collapsible.Content>
                <Stack gap="0px" my="10px">
                  {applicationQuestionAnswers.map((answer, index) => (
                    <React.Fragment key={answer.question_id}>
                      <Box px={{ base: '16px', md: '24px' }} py={{ base: '12px', md: '16px' }}>
                        <Flex alignItems="center" gap="12px" mb="8px">
                          <Text fontSize="14px" fontWeight="semibold" color="neutrals.500">
                            Question {index + 1}
                          </Text>
                          <Badge
                            fontSize="12px"
                            color="neutrals.700"
                            px="8px"
                            py="2px"
                            bg="neutrals.100"
                            borderRadius="full"
                          >
                            {getApplicationQuestionTypeLabel(answer.question_type)}
                          </Badge>
                        </Flex>
                        <Text fontSize="18px" color="neutrals.900" mb="8px" fontWeight="bold">
                          {answer.question}
                        </Text>
                        {answer.answer !== '' ? (
                          answer.question_type === 'yes_no' ? (
                            <Box
                              border="1px solid"
                              borderColor={answer.answer === 'yes' ? 'success.500' : 'error.500'}
                              borderRadius="12px"
                              p="4px 16px"
                              bg={answer.answer === 'yes' ? 'success.100' : 'error.100'}
                              w="fit-content"
                            >
                              <Text
                                fontSize="16px"
                                color={answer.answer === 'yes' ? 'success.500' : 'error.500'}
                              >
                                {answer.answer[0].toUpperCase() + answer.answer.slice(1)}
                              </Text>
                            </Box>
                          ) : answer.question_type === 'multiple_choice' ? (
                            <Box
                              border="1px solid"
                              borderColor="primary.300"
                              borderRadius="12px"
                              p="4px 16px"
                              bg="primary.50"
                              w="fit-content"
                            >
                              <Text fontSize="16px" color="primary.900" whiteSpace="pre-wrap">
                                {answer.answer}
                              </Text>
                            </Box>
                          ) : (
                            <Text fontSize="16px" color="neutrals.700" whiteSpace="pre-wrap">
                              {answer.answer}
                            </Text>
                          )
                        ) : (
                          <Text fontSize="16px" color="neutrals.500" fontStyle="italic">
                            No answer provided
                          </Text>
                        )}
                      </Box>
                      {index < applicationQuestionAnswers.length - 1 && (
                        <Box
                          as="hr"
                          width="100%"
                          border="none"
                          borderTop="1px solid"
                          borderColor="neutrals.200"
                          my={{ base: '6px', md: '8px' }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Stack>
              </Collapsible.Content>
            </Collapsible.Root>
          </Box>
        </Box>
      )}
      <Box color="neutrals.900" fontSize="18px" fontWeight="semibold" mb="20px">
        Requirements Match
      </Box>
      <Flex gap="20px" mb="20px">
        <RequirementMatchSummaryCard
          type="aligned"
          count={
            overviewAnalysisResult?.aligned_skills.length ||
            resumeAnalysisResult?.aligned_skills.length
          }
        />
        <RequirementMatchSummaryCard
          type="misaligned"
          count={
            overviewAnalysisResult?.gaps_or_misalignments.length ||
            resumeAnalysisResult?.gaps_or_misalignments.length
          }
        />
      </Flex>
      <Flex gap="20px">
        <RequirementMatchCollapsible
          title="Aligned Skills"
          matchType="aligned"
          items={overviewAnalysisResult?.aligned_skills || resumeAnalysisResult?.aligned_skills}
          isOpen={alignedItemsOpen}
          setOpen={setAlignedItemsOpen}
        />
        <RequirementMatchCollapsible
          title="Gaps or Misalignments"
          matchType="misaligned"
          items={
            overviewAnalysisResult?.gaps_or_misalignments ||
            resumeAnalysisResult?.gaps_or_misalignments
          }
          isOpen={misalignedItemsOpen}
          setOpen={setMisalignedItemsOpen}
        />
      </Flex>
      <Box color="neutrals.900" fontSize="18px" fontWeight="semibold" mb="12px">
        Follow-up Items
      </Box>
      <Flex gap="20px" flexWrap="wrap" align="stretch" w="100%">
        {followUpQuestions.map((item, index) => (
          <FollowUpItemsCard key={index} followUpItem={item} onClick={handleFollowUpClick} />
        ))}
      </Flex>
    </Flex>
  );
}

const RequirementMatchSummaryCard = ({
  type,
  count
}: {
  type: 'aligned' | 'misaligned';
  count: number;
}) => {
  const bgColor = type === 'aligned' ? 'success.100' : 'error.100';
  const icon =
    type === 'aligned' ? (
      <Check size={24} weight="bold" color="#0FA958" />
    ) : (
      <X size={24} weight="bold" color="#F97066" />
    );
  return (
    <Box borderRadius="12px" p="16px 12px" bg="neutrals.50">
      <Flex gap="16px">
        <Box borderRadius="12px" p="16px" bg={bgColor}>
          {icon}
        </Box>
        <Stack gap="4px" mr="32px">
          <Text fontSize="18px" fontWeight="semibold" color="neutrals.800">
            {count}
          </Text>
          <Text fontSize="14px" color="neutrals.800">
            {type === 'aligned' ? 'Aligned Skills' : 'Misalignments'}
          </Text>
        </Stack>
      </Flex>
    </Box>
  );
};
